import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import MainLayout from '../components/MainLayout'
import { getAdminUsers, migrateUser, backupUser, AdminUserInfo } from '../api/admin'

function formatTs(ts: number | null): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleString('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function AdminUsersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUserInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionMsg, setActionMsg] = useState('')
  const [busy, setBusy] = useState<string | null>(null) // user_id being acted on

  useEffect(() => {
    if (!user?.is_superuser) {
      navigate('/home', { replace: true })
    }
  }, [user, navigate])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAdminUsers()
      setUsers(data)
    } catch {
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const runAction = async (userId: string, action: 'migrate' | 'backup') => {
    setBusy(userId + action)
    setActionMsg('')
    try {
      if (action === 'migrate') await migrateUser(userId)
      else await backupUser(userId)
      setActionMsg(`${action === 'migrate' ? 'Migration' : 'Backup'} started for user.`)
    } catch {
      setActionMsg(`Failed to start ${action}.`)
    } finally {
      setBusy(null)
    }
  }

  if (!user?.is_superuser) return null

  return (
    <MainLayout>
      <div className="max-w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <button onClick={() => navigate('/admin')} className="hover:text-primary">Admin</button>
              <span>/</span>
              <span>Users</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-users-heading">User Management</h1>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {actionMsg && (
          <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm" data-testid="admin-action-msg">{actionMsg}</div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="admin-users-table">
              <thead className="bg-gray-50">
                <tr>
                  {['Email', 'Created', 'Total Notes', 'Empty', 'Migrated', 'Last Update', 'Last Note Backup', 'Last Backup', 'Backup OK', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 font-medium text-gray-900 max-w-[200px] truncate">{u.email}</td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{formatTs(u.created_time)}</td>
                    <td className="px-3 py-3 text-center font-semibold">{u.total_notes_count}</td>
                    <td className="px-3 py-3 text-center text-orange-600">{u.empty_notes_count || '—'}</td>
                    <td className="px-3 py-3 text-center text-green-700">{u.migrated_notes_count}</td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{formatTs(u.latest_note_time)}</td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{formatTs(u.latest_note_backup)}</td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{formatTs(u.latest_backup)}</td>
                    <td className="px-3 py-3 text-center">
                      {u.backed_up_success === null ? '—' : u.backed_up_success
                        ? <span className="text-green-600 font-medium">✓</span>
                        : <span className="text-red-500 font-medium">✗</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => runAction(u.id, 'migrate')}
                          disabled={busy === u.id + 'migrate'}
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Migrate GDrive → DB"
                          data-testid={`migrate-btn-${u.id}`}
                        >
                          {busy === u.id + 'migrate' ? '…' : 'Migrate'}
                        </button>
                        <button
                          onClick={() => runAction(u.id, 'backup')}
                          disabled={busy === u.id + 'backup'}
                          className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Backup DB → GDrive"
                          data-testid={`backup-btn-${u.id}`}
                        >
                          {busy === u.id + 'backup' ? '…' : 'Backup'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-gray-400">No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-4 text-xs text-gray-400">{users.length} user{users.length !== 1 ? 's' : ''} total</p>
      </div>
    </MainLayout>
  )
}


